import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  Calendar,
  MapPin,
  Banknote,
  Eye,
  X,
  CircleGauge,
} from "lucide-react";
import ExploreEventCard from "../../components/exploreEventCard/ExploreEventCard.jsx";
import { eventService } from "../../helper/eventService.js";
import HeroSectionSame from "../../components/heroSectionSame/HeroSectionSame.jsx";
import Footer from "../../components/footer/Footer.js";
import ExploreEventCardSkeleton from "../../components/skeletons/ExploreEventCardSkeleton.jsx";
import "./ExplorePage.css";

const ExplorePage = () => {
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPricing, setSelectedPricing] = useState("all");
  const [selectedDate, setSelectedDate] = useState("all");
  const [selectedVisibility, setSelectedVisibility] = useState("all");
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [sortBy, setSortBy] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);

  const eventsPerPage = 12;

  const [showFilters, setShowFilters] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "wedding", label: "Wedding" },
    { value: "birthday", label: "Birthday" },
    { value: "conference", label: "Conference" },
    { value: "meet-up", label: "Meet-up" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    fetchAllEvents(currentPage);
  }, [
    currentPage,
    searchQuery,
    selectedCategory,
    selectedPricing,
    selectedDate,
    selectedVisibility,
    showPastEvents,
    sortBy,
  ]);

  const getEventPrimaryDate = (event) => {
    if (event.eventDate) {
      return new Date(event.eventDate);
    }
    if (event.startDate) {
      return new Date(event.startDate);
    }
    if (event.multipleDates && event.multipleDates?.length > 0) {
      const sortedDates = event.multipleDates
        .map((d) => new Date(d.date))
        .sort((a, b) => a - b);
      return sortedDates[0];
    }
    return new Date(0);
  };

  const fetchAllEvents = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        page,
        perPage: eventsPerPage,
        sortBy,
        title: searchQuery.trim() || undefined,
        category: selectedCategory !== "all" ? selectedCategory : undefined,
        price:
          selectedPricing === "free"
            ? "free"
            : selectedPricing === "paid"
            ? "paid"
            : undefined,
        date: selectedDate !== "all" ? selectedDate : undefined,
        visibility:
          selectedVisibility !== "all" ? selectedVisibility : undefined,
        past: showPastEvents ? "true" : undefined,
      };

      const response = await eventService.getPublicEvents(filters);

      const { data: events, total, totalPages: serverTotalPages } = response;

      setAllEvents(events);
      setFilteredEvents(events);
      setTotalEvents(total);
      setTotalPages(serverTotalPages || 1);
      setCurrentPage(page);
    } catch (err) {
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const applyClientSideFilters = (eventsArray) => {
    let filtered = [...eventsArray];

    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (event) =>
          event.eventTitle?.toLowerCase().includes(query) ||
          event.eventDescription?.toLowerCase().includes(query) ||
          event.eventType?.toLowerCase().includes(query)
      );
    }

    if (selectedCategory && selectedCategory !== "all") {
      const predefinedCategories = [
        "wedding",
        "birthday",
        "conference",
        "meet-up",
      ];

      if (selectedCategory === "other") {
        filtered = filtered.filter((event) => {
          const eventType = event.eventType?.toLowerCase() || "";
          return !predefinedCategories.includes(eventType);
        });
      } else {
        filtered = filtered.filter(
          (event) =>
            event.eventType?.toLowerCase() === selectedCategory.toLowerCase()
        );
      }
    }

    if (selectedPricing && selectedPricing !== "all") {
      const isPaidEvent = selectedPricing === "paid";
      filtered = filtered.filter(
        (event) => event.isEntryFeeApplicable === isPaidEvent
      );
    }

    if (selectedDate && selectedDate !== "all") {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      filtered = filtered.filter((event) => {
        const eventDate = getEventPrimaryDate(event);

        switch (selectedDate) {
          case "today":
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return eventDate >= today && eventDate < tomorrow;

          case "this-week":
            const weekFromNow = new Date(now);
            weekFromNow.setDate(weekFromNow.getDate() + 7);
            return eventDate >= now && eventDate <= weekFromNow;

          case "this-month":
            const endOfMonth = new Date(
              now.getFullYear(),
              now.getMonth() + 1,
              0
            );
            return eventDate >= now && eventDate <= endOfMonth;

          case "upcoming":
            return eventDate >= now;

          default:
            return true;
        }
      });
    }
    if (selectedVisibility && selectedVisibility !== "all") {
      filtered = filtered.filter(
        (event) => event.visibility === selectedVisibility
      );
    }

    if (!showPastEvents) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      filtered = filtered.filter((event) => {
        const eventDate = getEventPrimaryDate(event);
        return eventDate >= now;
      });
    }

    return filtered;
  };

  const applySorting = (eventsArray) => {
    const sorted = [...eventsArray];

    switch (sortBy) {
      case "date-asc":
        return sorted.sort((a, b) => {
          const dateA = getEventPrimaryDate(a);
          const dateB = getEventPrimaryDate(b);
          return dateA - dateB;
        });

      case "date-desc":
        return sorted.sort((a, b) => {
          const dateA = getEventPrimaryDate(a);
          const dateB = getEventPrimaryDate(b);
          return dateB - dateA;
        });

      case "title-asc":
        return sorted.sort((a, b) =>
          (a.eventTitle || "").localeCompare(b.eventTitle || "")
        );

      case "title-desc":
        return sorted.sort((a, b) =>
          (b.eventTitle || "").localeCompare(a.eventTitle || "")
        );

      case "created-desc":
        return sorted.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });

      default:
        return sorted;
    }
  };

  const applyFiltersAndSortToEvents = (eventsArray) => {
    let processed = applyClientSideFilters(eventsArray);
    processed = applySorting(processed);

    setFilteredEvents(processed);
    setTotalEvents(processed.length);

    const calculatedTotalPages = Math.ceil(processed.length / eventsPerPage);
    setTotalPages(calculatedTotalPages || 1);
  };

  const handleSearchChange = (e) => {
    setCurrentPage(1);
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = (category) => {
    setCurrentPage(1);
    setSelectedCategory(category);
  };

  const handlePricingChange = (pricing) => {
    setCurrentPage(1);
    setSelectedPricing(pricing);
  };

  const handleDateChange = (date) => {
    setCurrentPage(1);
    setSelectedDate(date);
  };

  const handleSortChange = (e) => {
    setCurrentPage(1);
    setSortBy(e.target.value);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("all");
    setSelectedPricing("all");
    setSelectedDate("all");
    setSelectedVisibility("all");
    setShowPastEvents(false);
    setSortBy("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchAllEvents(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleNavClick = (page) => {
    navigate(page);
  };

  const handleLogin = () => {
    console.log("Login clicked");
    navigate("/onboarding");
  };

  const handleGetStarted = () => {
    console.log("Get Started clicked");
    navigate("/onboarding");
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowFilters(false);
    }
  };

  return (
    <div className="explore-page">
      <div className="explore-page-header">
        <HeroSectionSame
          variant="explore"
          activeNav="explore"
          onNavClick={handleNavClick}
          onLoginClick={handleLogin}
          onGetStartedClick={handleGetStarted}
        />
      </div>

      <div className="explore-page-container">
        <div className="explore-page-content">
          {showFilters && (
            <div
              className="explore-page-filters-overlay"
              onClick={handleOverlayClick}
            />
          )}

          <aside
            className={`explore-page-filters-sidebar ${
              showFilters ? "explore-page-mobile-visible" : ""
            }`}
          >
            <div className="explore-page-filters-header">
              <h3>Filters</h3>
              <button
                className="explore-page-clear-filters"
                onClick={handleClearFilters}
              >
                Clear All
              </button>
              <button
                className="explore-page-filters-close-btn"
                onClick={handleCloseFilters}
                aria-label="Close filters"
              >
                <X size={24} />
              </button>
            </div>

            <div className="explore-page-filter-group">
              <label className="explore-page-filter-label">
                <Search size={18} />
                Search Events
              </label>
              <input
                type="text"
                placeholder="Search by title..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="explore-page-search-input"
              />
            </div>

            <div className="explore-page-filter-group">
              <label className="explore-page-filter-label">
                <CircleGauge size={18} />
                Category
              </label>
              <div className="explore-page-filter-options">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    className={`explore-page-filter-option ${
                      selectedCategory === cat.value
                        ? "explore-page-active"
                        : ""
                    }`}
                    onClick={() => handleCategoryChange(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="explore-page-filter-group">
              <label className="explore-page-filter-label">
                <Eye size={18} />
                Event Visibility
              </label>
              <div className="explore-page-filter-options">
                <button
                  className={`explore-page-filter-option ${
                    selectedVisibility === "all" ? "explore-page-active" : ""
                  }`}
                  onClick={() => setSelectedVisibility("all")}
                >
                  All Events
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedVisibility === "public" ? "explore-page-active" : ""
                  }`}
                  onClick={() => setSelectedVisibility("public")}
                >
                  Public
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedVisibility === "private"
                      ? "explore-page-active"
                      : ""
                  }`}
                  onClick={() => setSelectedVisibility("private")}
                >
                  Private
                </button>
              </div>
            </div>

            <div className="explore-page-filter-group">
              <label className="explore-page-filter-label">
                <Banknote size={18} />
                Pricing
              </label>
              <div className="explore-page-filter-options">
                <button
                  className={`explore-page-filter-option ${
                    selectedPricing === "all" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handlePricingChange("all")}
                >
                  All Events
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedPricing === "free" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handlePricingChange("free")}
                >
                  Free Events
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedPricing === "paid" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handlePricingChange("paid")}
                >
                  Paid Events
                </button>
              </div>
            </div>

            <div className="explore-page-filter-group">
              <label className="explore-page-filter-label">
                <Calendar size={18} />
                Date
              </label>
              <div className="explore-page-filter-options">
                <button
                  className={`explore-page-filter-option ${
                    selectedDate === "all" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handleDateChange("all")}
                >
                  All Dates
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedDate === "today" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handleDateChange("today")}
                >
                  Today
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedDate === "this-week" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handleDateChange("this-week")}
                >
                  This Week
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedDate === "this-month" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handleDateChange("this-month")}
                >
                  This Month
                </button>
                <button
                  className={`explore-page-filter-option ${
                    selectedDate === "upcoming" ? "explore-page-active" : ""
                  }`}
                  onClick={() => handleDateChange("upcoming")}
                >
                  Upcoming
                </button>
              </div>
            </div>

            <div className="explore-page-filter-group">
              <label className="explore-page-filter-label">
                <Calendar size={18} />
                Past Events
              </label>
              <div className="explore-page-filter-toggle-wrapper">
                <label className="explore-page-toggle-label">
                  <input
                    type="checkbox"
                    checked={showPastEvents}
                    onChange={(e) => setShowPastEvents(e.target.checked)}
                    className="explore-page-toggle-checkbox"
                  />
                  <span className="explore-page-toggle-switch"></span>
                  <span className="explore-page-toggle-text">
                    Show past events
                  </span>
                </label>
              </div>
            </div>

            <button
              className="explore-page-mobile-close-filters"
              onClick={() => setShowFilters(false)}
            >
              Apply Filters
            </button>
          </aside>

          <main className="explore-page-main">
            <div className="explore-page-toolbar">
              <div className="explore-page-toolbar-left">
                <button
                  className="explore-page-mobile-filter-toggle"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={20} />
                  Filters
                </button>
                {!initialLoading && (
                  <p className="explore-page-results-count">
                    {totalEvents} {totalEvents === 1 ? "event" : "events"} found
                  </p>
                )}
              </div>
              <>
                {loading && (
                  <div className="explore-page-loading-spinner">
                    <div className="explore-page-spinner"></div>
                  </div>
                )}
              </>
              <div className="explore-page-toolbar-right">
                <label
                  htmlFor="sort-select"
                  className="explore-page-sort-label"
                >
                  Sort by:
                </label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={handleSortChange}
                  className="explore-page-sort-select"
                >
                  <option value="date-asc">Date (Earliest First)</option>
                  <option value="date-desc">Date (Latest First)</option>
                  <option value="title-asc">Title (A-Z)</option>
                  <option value="title-desc">Title (Z-A)</option>
                  <option value="created-desc">Recently Added</option>
                </select>
              </div>
            </div>

            {initialLoading ? (
              <div className="explore-page-events-grid">
                {Array.from({ length: 12 }).map((_, i) => (
                  <ExploreEventCardSkeleton key={i} />
                ))}
              </div>
            ) : error ? (
              <div className="explore-page-error-container">
                <p>{error}</p>
                <button
                  onClick={() => fetchAllEvents(currentPage)}
                  className="explore-page-retry-button"
                >
                  Retry
                </button>
              </div>
            ) : allEvents?.length === 0 ? (
              <div className="explore-page-no-results">
                <MapPin size={48} />
                <h3>No events found</h3>
                <p>Try adjusting your filters or search query</p>
                <button
                  onClick={handleClearFilters}
                  className="explore-page-btn-clear-filters"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <>
                <div className="explore-page-events-grid">
                  {allEvents &&
                    allEvents.map((event) => (
                      <ExploreEventCard
                        key={event._id || event.id}
                        event={event}
                        showShareButton={false}
                      />
                    ))}
                </div>

                {totalPages > 1 && (
                  <div className="explore-page-pagination">
                    <button
                      className="explore-page-pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>

                    <div className="explore-page-pagination-numbers">
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`explore-page-pagination-number ${
                                page === currentPage
                                  ? "explore-page-active"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span
                              key={page}
                              className="explore-page-pagination-ellipsis"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                    </div>

                    <button
                      className="explore-page-pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ExplorePage;
